// components/PropertyForm.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as PropertyIcon
} from '@mui/icons-material';
import {
  PropertyForm,
  PropertyExpandedSections,
  PropertySectionKey,
  PropertyCompactTextFieldProps,
  PropertySectionHeaderProps
} from '../simulationTypes';

interface PropertyFormComponentProps {
  form: PropertyForm;
  handleInputChange: (field: keyof PropertyForm, value: number | string) => void;
  expandedSections: PropertyExpandedSections;
  toggleSection: (section: PropertySectionKey) => void;
  runPropertySimulation: () => void;
  loading: boolean;
  error: string | null;
}

const CompactTextField: React.FC<
  PropertyCompactTextFieldProps & { form: PropertyForm; handleInputChange: any }
> = ({
  label,
  field,
  type = 'number',
  prefix = '',
  suffix = '',
  helperText = '',
  form,
  handleInputChange
}) => (
  <TextField
    label={label}
    type={type}
    size="small"
    fullWidth
    value={type === 'number' && suffix === '%' ? (form[field] as number) * 100 : form[field]}
    onChange={(e) => {
      const value =
        type === 'number'
          ? suffix === '%'
            ? parseFloat(e.target.value) / 100
            : parseFloat(e.target.value)
          : e.target.value;
      handleInputChange(field, value);
    }}
    InputProps={{
      startAdornment: prefix ? (
        <InputAdornment position="start">{prefix}</InputAdornment>
      ) : undefined,
      endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined
    }}
    helperText={helperText}
    sx={{ mb: 1 }}
  />
);

const SectionHeader: React.FC<
  PropertySectionHeaderProps & { expandedSections: PropertyExpandedSections; toggleSection: any }
> = ({ title, section, icon, expandedSections, toggleSection }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      p: 1,
      borderRadius: 1,
      bgcolor: expandedSections[section] ? '#e3f2fd' : 'transparent',
      '&:hover': { bgcolor: '#f5f5f5' }
    }}
    onClick={() => toggleSection(section)}
  >
    {icon}
    <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1, flex: 1 }}>
      {title}
    </Typography>
    <IconButton size="small">
      {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </IconButton>
  </Box>
);

const PropertyFormComponent: React.FC<PropertyFormComponentProps> = ({
  form,
  handleInputChange,
  expandedSections,
  toggleSection,
  runPropertySimulation,
  loading,
  error
}) => {
  return (
    <Card elevation={2} sx={{ height: 'fit-content', position: 'sticky', top: 16,p:0.5 }}>
      <CardContent sx={{ p: {xs:0.5, sm: 2} }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PropertyIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold" color="primary">
            Property Setup
          </Typography>
        </Box>

        {/* Basic Config */}
        <SectionHeader
          title="Basic Config"
          section="basic"
          icon={<PropertyIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.basic}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Iterations"
                  field="iterations"
                  helperText="Monte Carlo runs"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Years"
                  field="years"
                  helperText="Investment period"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <CompactTextField
                  label="Property Name"
                  field="assetName"
                  type="text"
                  helperText="Property description"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Purchase Price"
                  field="purchasePrice"
                  prefix="$"
                  helperText="Total cost"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Stamp Duty"
                  field="stampDuty"
                  prefix="$"
                  helperText="Govt fees"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Financing */}
        <SectionHeader
          title="Financing"
          section="financing"
          icon={<PropertyIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.financing}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Loan Amount"
                  field="loanAmount"
                  prefix="$"
                  helperText="Mortgage"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Loan Term"
                  field="termYears"
                  helperText="Years"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Interest Min"
                  field="interestRateMin"
                  suffix="%"
                  helperText="Min rate"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Interest Max"
                  field="interestRateMax"
                  suffix="%"
                  helperText="Max rate"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Rental Income */}
        <SectionHeader
          title="Rental Income"
          section="income"
          icon={<PropertyIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.income}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Monthly Rent"
                  field="monthlyIncomeAmount"
                  prefix="$"
                  helperText="Per month"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Council Rates"
                  field="councilRates"
                  prefix="$"
                  helperText="Annual"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Growth Min"
                  field="incomeGrowthMin"
                  suffix="%"
                  helperText="Min growth"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Growth Max"
                  field="incomeGrowthMax"
                  suffix="%"
                  helperText="Max growth"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Insurance"
                  field="insurance"
                  prefix="$"
                  helperText="Annual insurance"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Maintenance"
                  field="maintenance"
                  prefix="$"
                  helperText="Annual maintenance"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Market Growth */}
        <SectionHeader
          title="Market Growth"
          section="market"
          icon={<PropertyIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.market}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Capital Min"
                  field="capitalGrowthMin"
                  suffix="%"
                  helperText="Conservative"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Capital Max"
                  field="capitalGrowthMax"
                  suffix="%"
                  helperText="Optimistic"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Volatility"
                  field="volatilityStdDev"
                  suffix="%"
                  helperText="Market risk"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Recession %"
                  field="recessionProb"
                  suffix="%"
                  helperText="Probability"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Risk Factors */}
        <SectionHeader
          title="Risk Factors"
          section="risk"
          icon={<PropertyIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.risk}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Vacancy Rate"
                  field="vacancyRate"
                  suffix="%"
                  helperText="Empty periods"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Default Risk"
                  field="defaultProbability"
                  suffix="%"
                  helperText="Payment issues"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Liquidity Discount"
                  field="liquidityDiscount"
                  suffix="%"
                  helperText="Sale discount"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Management Fee"
                  field="managementFeePercent"
                  suffix="%"
                  helperText="Annual fee"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={runPropertySimulation}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PropertyIcon />}
            fullWidth
            sx={{ fontWeight: 'bold' }}
          >
            {loading ? 'Running Simulation...' : 'Run Property Simulation'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyFormComponent;
